import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useMediaQuery } from '@/lib/hooks';
import { RAIL_MAX, RAIL_MIN, useRailWidth } from '@/lib/rail';
import { cn } from '@/lib/utils';

/**
 * The app's right-hand rail: a panel pinned to the right edge of the shell, full height and
 * resizable exactly like the left sidebar, that pages fill with detail views (a contact, a message,
 * a bulk editor). Pages hand their content over with `useRailSlot`; the shell renders it through
 * `RailOutlet`. Below the `lg` breakpoint the same content opens as a full-width drawer from the
 * right, so phones get a proper detail screen instead of a squeezed column.
 *
 * The slot is a tiny external store rather than React state on the shell: pages write to it from an
 * effect on every render (their JSX is fresh each time), and only `RailOutlet` subscribes, so the
 * write never re-renders the page that produced it.
 */

interface RailSlot {
  content: ReactNode;
  /** Called when the drawer is dismissed on small screens (swipe, overlay, escape). */
  onClose?: () => void;
  /** Accessible name for the drawer. */
  label?: string;
}

const listeners = new Set<() => void>();
let slot: RailSlot | null = null;

function setSlot(next: RailSlot | null): void {
  slot = next;
  for (const l of listeners) l();
}
function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Show `content` in the rail while the calling page is mounted; pass null to hide it. */
export function useRailSlot(content: ReactNode | null, onClose?: () => void, label = 'Details'): void {
  useEffect(() => {
    setSlot(content === null || content === undefined || content === false ? null : { content, onClose, label });
  });
  useEffect(() => () => setSlot(null), []);
}

export function RailOutlet() {
  const current = useSyncExternalStore(subscribe, () => slot, () => null);
  const rail = useRailWidth();
  const docked = useMediaQuery('(min-width: 1024px)');
  if (!current) return null;

  if (!docked) {
    return (
      <Sheet open onOpenChange={(o) => !o && current.onClose?.()}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md" aria-label={current.label}>
          <SheetTitle className="sr-only">{current.label}</SheetTitle>
          {current.content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <aside className="relative hidden shrink-0 lg:block" style={{ width: rail.width }} aria-label={current.label}>
      <div className="bg-card text-card-foreground sticky top-0 flex h-dvh flex-col border-l">{current.content}</div>
      <RailResizeHandle />
    </aside>
  );
}

/** Drag the rail's left edge; double-click resets; arrow keys nudge. Mirrors the sidebar's handle. */
function RailResizeHandle() {
  const rail = useRailWidth();
  const dragging = useRef(false);
  const [active, setActive] = useState(false);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragging.current = true;
      setActive(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      const startX = e.clientX;
      const startW = rail.width;
      const move = (ev: PointerEvent) => {
        if (dragging.current) rail.setWidth(startW + (startX - ev.clientX));
      };
      const up = () => {
        dragging.current = false;
        setActive(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    },
    [rail],
  );

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      aria-valuemin={RAIL_MIN}
      aria-valuemax={RAIL_MAX}
      aria-valuenow={rail.width}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onDoubleClick={rail.reset}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') rail.setWidth(rail.width + 16);
        else if (e.key === 'ArrowRight') rail.setWidth(rail.width - 16);
        else if (e.key === 'Home') rail.reset();
        else return;
        e.preventDefault();
      }}
      className="group absolute inset-y-0 -left-1.5 z-40 w-3 cursor-col-resize outline-none"
    >
      <div className={cn('group-hover:bg-primary group-focus-visible:bg-primary absolute inset-y-0 left-1 w-px transition-colors', active ? 'bg-primary' : 'bg-transparent')} />
    </div>
  );
}
